import React, { useState } from "react";
import { Box, Link, Modal, SpaceBetween } from "@amzn/awsui-components-react";
import {
  InvestigationAttachment,
  Stage,
} from "../../../lusca-search-api/generated-src";
import { InvestigationsApiFactory } from "../../../lusca-search-api/LuscaSearchApiFactory";
import { formatBytes, isImageType } from "./attachmentUtils";
import { formatTimestamp } from "../investigationUtils";
import "./Attachments.scss";

interface AttachmentListProps {
  stage: Stage;
  investigationId: string;
  attachments?: InvestigationAttachment[];
  /** Links only (images open the lightbox), for dense places like the audit log table. */
  compact?: boolean;
}

/**
 * Attachments of a posted comment or a saved conclusion: images as thumbnails that open in a
 * lightbox, everything else as download links. The URLs are presigned and expire after an hour;
 * a broken image (page left open) or a click after expiry fetches a fresh one.
 */
export const AttachmentList: React.FC<AttachmentListProps> = ({
  stage,
  investigationId,
  attachments,
  compact = false,
}) => {
  const [fresh, setFresh] = useState<Record<string, InvestigationAttachment>>(
    {}
  );
  const [open, setOpen] = useState<InvestigationAttachment | null>(null);
  const current = (a: InvestigationAttachment) =>
    fresh[a.key] ? { ...a, ...fresh[a.key] } : a;
  const items = (attachments || []).map(current);
  if (!items.length) return null;

  const refresh = async (a: InvestigationAttachment) => {
    if (fresh[a.key]) return fresh[a.key];
    const { data } =
      await InvestigationsApiFactory().getInvestigationArtifactUrl(
        stage,
        investigationId,
        a.key
      );
    setFresh((prev) => ({ ...prev, [a.key]: data }));
    return data;
  };
  const expired = (a: InvestigationAttachment) =>
    !a.url || (!!a.urlExpiresAt && Date.parse(a.urlExpiresAt) < Date.now());
  const openFile = (a: InvestigationAttachment) => {
    if (!expired(a)) {
      window.open(a.url, "_blank", "noopener,noreferrer");
      return;
    }
    // Open synchronously (popup blockers), then point it at the fresh URL.
    const tab = window.open("", "_blank");
    refresh(a)
      .then((data) => {
        if (tab && data.url) tab.location.href = data.url;
      })
      .catch(() => tab?.close());
  };

  const images = compact ? [] : items.filter((a) => isImageType(a.contentType));
  const files = compact
    ? items
    : items.filter((a) => !isImageType(a.contentType));
  const describe = (a: InvestigationAttachment) =>
    [
      formatBytes(a.size),
      a.uploadedBy && `uploaded by ${a.uploadedBy}`,
      a.uploadedAt && formatTimestamp(a.uploadedAt),
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <SpaceBetween size="xs">
      {images.length > 0 && (
        <div className="attachment-thumbs" data-testid="attachment-images">
          {images.map((a) => (
            <button
              key={a.key}
              type="button"
              className="attachment-thumb"
              title={a.fileName}
              aria-label={`View ${a.fileName || "image"}`}
              onClick={() => setOpen(a)}
            >
              <img
                src={a.url}
                alt={a.fileName || "attachment"}
                loading="lazy"
                onError={() => {
                  if (!fresh[a.key]) refresh(a).catch(console.error);
                }}
              />
            </button>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <SpaceBetween size="xxs">
          {files.map((a) => (
            <Box key={a.key} fontSize="body-s">
              <Link
                href={a.url || "#"}
                external={!isImageType(a.contentType)}
                onFollow={(e) => {
                  e.preventDefault();
                  if (isImageType(a.contentType)) setOpen(a);
                  else openFile(a);
                }}
              >
                {a.fileName || "attachment"}
              </Link>{" "}
              <Box variant="span" color="text-body-secondary" fontSize="body-s">
                {formatBytes(a.size)}
              </Box>
            </Box>
          ))}
        </SpaceBetween>
      )}
      {open && (
        <Modal
          visible
          size="max"
          onDismiss={() => setOpen(null)}
          header={open.fileName || "Attachment"}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="s" alignItems="center">
                <Box color="text-body-secondary" fontSize="body-s">
                  {describe(current(open))}
                </Box>
                <Link
                  external
                  href={current(open).url || "#"}
                  onFollow={(e) => {
                    e.preventDefault();
                    openFile(current(open));
                  }}
                >
                  Open original
                </Link>
              </SpaceBetween>
            </Box>
          }
        >
          <div className="attachment-lightbox">
            <img src={current(open).url} alt={open.fileName || "attachment"} />
          </div>
        </Modal>
      )}
    </SpaceBetween>
  );
};

export default AttachmentList;
